import { GA4_REFUSALS, Ga4OwnershipError } from '@/api/ga4'
import {
  classifyNotConfigured,
  classifyProviderDown,
  classifyRateLimited,
  classifyRefusal,
  pickWording,
  screenErrors,
  type NotConfiguredKind,
  type ProviderDownKind,
  type RateLimitedKind,
  type RefusalKind,
} from '@/features/ga4-shared/errors'

import { ownershipMismatchMessage } from './ownership'

/** What the person was doing. Each action owns what a 403, 404, 409 or 429 means to it. */
export type Ga4Action = 'connect' | 'manage'

type ConnectKind = RefusalKind | ProviderDownKind | RateLimitedKind | NotConfiguredKind | 'forbidden' | 'website_gone' | 'already_connected'
/** `cooldown` is the import limit: only this screen has an import button, so only it words a 429 that way. */
type ManageKind = ProviderDownKind | NotConfiguredKind | 'cooldown' | 'forbidden' | 'property_gone'

const FORBIDDEN = 'Your role cannot manage integrations. Ask an Owner or Administrator to do this.'

/** Connecting: a 404 is the website that was picked, a 409 a property that is already there. */
const connectMessage = screenErrors<ConnectKind>({
  status: {
    403: (error) => (GA4_REFUSALS.grantMissing.test(error.message) ? 'grant_missing' : 'forbidden'),
    404: (error) => (GA4_REFUSALS.propertyNotFound.test(error.message) ? 'property_not_found' : 'website_gone'),
    409: (error) => (GA4_REFUSALS.alreadyConnected.test(error.message) ? 'already_connected' : null),
  },
  rules: [classifyRefusal, classifyRateLimited, classifyProviderDown, classifyNotConfigured],
  wording: {
    ...pickWording('grant_missing', 'property_not_found', 'ownership_not_proven', 'provider_down', 'rate_limited', 'not_configured'),
    forbidden: FORBIDDEN,
    website_gone: 'That website is no longer available on your account. Choose another, or leave the property unlinked.',
    already_connected: 'That property is already connected. Reload the page to see it.',
  },
})

/** Run import now and Disconnect: a 404 is the property, a 429 the import limit, and a 409 says why in the server's own words. */
const manageMessage = screenErrors<ManageKind>({
  status: { 403: 'forbidden', 404: 'property_gone', 429: 'cooldown' },
  rules: [classifyProviderDown, classifyNotConfigured],
  wording: {
    ...pickWording('provider_down', 'not_configured'),
    cooldown:
      'An import of this property was requested less than ten minutes ago. Run import now is available again shortly; the daily import keeps running meanwhile.',
    forbidden: FORBIDDEN,
    property_gone: 'That property is no longer connected to this account. Reload the page to see the current list.',
  },
})

/**
 * A connect refusal the server enriched with the hosts it saw (`Ga4OwnershipError`):
 * named directly, in place of the shared generic `ownership_not_proven` wording.
 * `GA4_REFUSALS.ownershipNotProven` still gates it, so a refusal that merely
 * carries the two arrays for an unrelated reason cannot hijack this wording.
 */
function ownershipMismatchFromError(error: unknown): string | null {
  if (!(error instanceof Ga4OwnershipError)) return null
  if (!GA4_REFUSALS.ownershipNotProven.test(error.message)) return null
  return ownershipMismatchMessage(error.streamHosts, error.websiteHosts)
}

export function ga4ErrorMessage(error: unknown, email: string | null, fallback: string, action: Ga4Action = 'manage'): string {
  if (action === 'connect') {
    const mismatch = ownershipMismatchFromError(error)
    if (mismatch) return mismatch
  }
  return (action === 'connect' ? connectMessage : manageMessage)(error, email, fallback)
}

export function Ga4Error({
  error,
  email,
  fallback,
  action = 'manage',
}: {
  error: unknown
  email: string | null
  fallback: string
  action?: Ga4Action
}) {
  if (!error) return null
  return (
    <p role="alert" className="ci-integration__error">
      {ga4ErrorMessage(error, email, fallback, action)}
    </p>
  )
}
